import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { environment } from '../../../environments/environment';
import { PlanMarker, SymbolShapeConfig } from './models/plan-marker.interface';
import { PlanConfig, PlanState } from './models/plan-config.interface';
import { RoomIndicator } from '../../plan-edit/plan-edit.component';

@Component({
  selector: 'app-interactive-plan',
  templateUrl: './interactive-plan.component.html',
  styleUrls: ['./interactive-plan.component.scss']
})
export class InteractivePlanComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() planImageUrl: string = '';
  @Input() roomIndicators: RoomIndicator[] = [];
  @Input() width: number = 600;
  @Input() height: number = 400;
  @Input() zoomLevel: number = 100;
  
  @Output() markerPlaced = new EventEmitter<PlanMarker>();
  @Output() markerMoved = new EventEmitter<PlanMarker>();
  @Output() markerRemoved = new EventEmitter<string>();
  @Output() zoomChanged = new EventEmitter<number>();
  @Output() planExported = new EventEmitter<string>();

  @ViewChild('konvaContainer', { static: true }) konvaContainer!: ElementRef;

  // Konva objects
  stage: Konva.Stage | null = null;
  backgroundLayer: Konva.Layer | null = null;
  markersLayer: Konva.Layer | null = null;
  uiLayer: Konva.Layer | null = null;
  planImage?: Konva.Image;

  // Configuration
  planConfig: PlanConfig = {
    width: 600,
    height: 400,
    scale: 1,
    draggable: true,
    zoomEnabled: true,
    minZoom: 0.25,
    maxZoom: 3
  };

  planState: PlanState = {
    markers: [],
    selectedMarker: null,
    isDragging: false,
    zoomLevel: 100,
    panOffset: { x: 0, y: 0 },
    showTooltip: false,
    tooltipPosition: { x: 0, y: 0 },
    tooltipMarker: null
  };

  // Symbol shape mapping from environment
  symbolShapeMap: { [key: string]: SymbolShapeConfig } = (environment as any).indikatorSymbolShapeMap || {};

  constructor(private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    // Component initialization
  }

  ngAfterViewInit(): void {
    // Initialize Konva after view is initialized with a small delay
    setTimeout(() => {
      this.initializeKonva();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planImageUrl']) {
      if (changes['planImageUrl'].currentValue && this.backgroundLayer) {
        this.loadPlanImage();
      } else {
        // Clear existing image if URL is empty
        if (this.planImage && this.backgroundLayer) {
          this.planImage.destroy();
          this.planImage = null as any;
          this.backgroundLayer.draw();
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.stage) {
      this.stage.destroy();
    }
  }

  private initializeKonva(): void {
    // Check if ViewChild is available
    if (!this.konvaContainer || !this.konvaContainer.nativeElement) {
      console.error('Konva container not available');
      return;
    }

    console.log('Initializing Konva with container:', this.konvaContainer.nativeElement);
    
    // Create stage
    this.stage = new Konva.Stage({
      container: this.konvaContainer.nativeElement,
      width: this.planConfig.width,
      height: this.planConfig.height,
      draggable: this.planConfig.draggable
    });

    // Create layers
    this.backgroundLayer = new Konva.Layer();
    this.markersLayer = new Konva.Layer();
    this.uiLayer = new Konva.Layer();

    this.stage.add(this.backgroundLayer);
    this.stage.add(this.markersLayer);
    this.stage.add(this.uiLayer);

    // Add event listeners
    this.setupEventListeners();

    // Load image if URL is already available
    if (this.planImageUrl) {
      this.loadPlanImage();
    }
    
    console.log('Konva initialized successfully');
  }

  private setupEventListeners(): void {
    if (!this.stage) return;
    
    // Stage events
    this.stage.on('wheel', (e) => this.handleZoom(e));
    this.stage.on('dragend', (e) => this.handlePan(e));

    // Drop events
    this.stage.on('drop', (e) => this.handleDrop(e));
    this.stage.on('dragover', (e) => this.handleDragOver(e));
  }

  private loadPlanImage(): void {
    if (!this.planImageUrl || !this.backgroundLayer) {
      console.log('Cannot load plan image:', { planImageUrl: this.planImageUrl, backgroundLayer: !!this.backgroundLayer });
      return;
    }

    console.log('Loading plan image:', this.planImageUrl);
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      console.log('Plan image loaded successfully');
      // Ensure layers are initialized
      if (!this.backgroundLayer) {
        console.error('Background layer not initialized');
        return;
      }

      // Calculate the image dimensions to fit within the container while maintaining aspect ratio
      const containerWidth = this.planConfig.width;
      const containerHeight = this.planConfig.height;
      const imageAspectRatio = image.width / image.height;
      const containerAspectRatio = containerWidth / containerHeight;

      let imageWidth, imageHeight, imageX, imageY;

      if (imageAspectRatio > containerAspectRatio) {
        // Image is wider than container
        imageWidth = containerWidth;
        imageHeight = containerWidth / imageAspectRatio;
        imageX = 0;
        imageY = (containerHeight - imageHeight) / 2;
      } else {
        // Image is taller than container
        imageHeight = containerHeight;
        imageWidth = containerHeight * imageAspectRatio;
        imageX = (containerWidth - imageWidth) / 2;
        imageY = 0;
      }

      this.planImage = new Konva.Image({
        image: image,
        x: imageX,
        y: imageY,
        width: imageWidth,
        height: imageHeight,
        draggable: false,
        listening: true
      });

      this.backgroundLayer.add(this.planImage);
      this.backgroundLayer.draw();
    };
    image.onerror = () => {
      console.error('Failed to load plan image:', this.planImageUrl);
    };
    image.src = this.planImageUrl;
  }

  // Zoom functionality
  private handleZoom(e: KonvaEventObject<WheelEvent>): void {
    e.evt.preventDefault();

    const scaleBy = 1.1;
    const stage = e.target.getStage();
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(this.planConfig.minZoom, Math.min(this.planConfig.maxZoom, newScale));

    this.planState.zoomLevel = clampedScale * 100;
    this.zoomChanged.emit(this.planState.zoomLevel);

    stage.scale({ x: clampedScale, y: clampedScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    stage.position(newPos);
    stage.batchDraw();
  }

  private handlePan(e: KonvaEventObject<DragEvent>): void {
    this.planState.panOffset = {
      x: e.target.x(),
      y: e.target.y()
    };
  }

  // Drag and drop functionality
  private handleDragOver(e: KonvaEventObject<DragEvent>): void {
    e.evt.preventDefault();
  }

  private handleDrop(e: KonvaEventObject<DragEvent>): void {
    e.evt.preventDefault();
    
    const data = e.evt.dataTransfer?.getData('text/plain');
    if (!data) return;

    try {
      const indicator: RoomIndicator = JSON.parse(data);
      if (!this.stage) return;
      const position = this.stage.getPointerPosition();
      
      if (position) {
        this.placeMarker(indicator, position.x, position.y);
      }
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }

  public placeMarker(indicator: RoomIndicator, x: number, y: number): void {
    const marker: PlanMarker = {
      id: `marker_${Date.now()}_${indicator.id}`,
      indicator: indicator,
      x: x,
      y: y,
      scale: 1,
      rotation: 0,
      visible: true,
      locked: false,
      shape: indicator.indikator?.symbol_id || '1'
    };

    this.planState.markers.push(marker);
    this.createMarkerShape(marker);
    this.markerPlaced.emit(marker);
    
    this.snackBar.open(`Indikator "${indicator.name}" wurde platziert`, 'Schließen', { duration: 2000 });
  }

  private createMarkerShape(marker: PlanMarker): void {
    const group = new Konva.Group({
      id: marker.id,
      x: marker.x,
      y: marker.y,
      draggable: true
    });

    const shape = this.createShape(marker);
    group.add(shape);

    // Add event listeners
    group.on('dragend', (e) => this.handleMarkerDragEnd(e, marker));
    group.on('click', (e) => this.handleMarkerClick(e, marker));
    group.on('contextmenu', (e) => this.handleMarkerRightClick(e, marker));

    if (this.markersLayer) {
      this.markersLayer.add(group);
      this.markersLayer.draw();
    }
  }

  private createShape(marker: PlanMarker): Konva.Shape {
    const config = this.getSymbolConfig(marker.shape);
    const halfSize = config.size / 2;

    switch (config.shape) {
      case 'circle':
        return new Konva.Circle({
          radius: halfSize,
          fill: config.fill,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });

      case 'rect':
        return new Konva.Rect({
          width: config.size,
          height: config.size,
          x: -halfSize,
          y: -halfSize,
          fill: config.fill,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          cornerRadius: 4,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });

      case 'polygon':
        return new Konva.Line({
          points: config.points || [0, -halfSize, halfSize, halfSize, -halfSize, halfSize],
          closed: true,
          fill: config.fill,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          shadowColor: 'black',
          shadowBlur: 4,
          shadowOffset: { x: 2, y: 2 },
          shadowOpacity: 0.3
        });

      case 'image':
        const image = new Image();
        image.onload = () => {
          const konvaImage = new Konva.Image({
            image: image,
            width: config.size,
            height: config.size,
            x: -halfSize,
            y: -halfSize,
            shadowColor: 'black',
            shadowBlur: 4,
            shadowOffset: { x: 2, y: 2 },
            shadowOpacity: 0.3
          });
          // Replace the placeholder with the actual image
          const group = konvaImage.getParent();
          if (group) {
            group.destroyChildren();
            group.add(konvaImage);
            group.draw();
          }
        };
        image.src = config.imageUrl || '/assets/symbols/layer.png';
        
        // Return a placeholder circle while image loads
        return new Konva.Circle({
          radius: halfSize,
          fill: '#f3f4f6',
          stroke: config.stroke,
          strokeWidth: config.strokeWidth
        });

      default:
        return new Konva.Circle({
          radius: halfSize,
          fill: '#ffffff',
          stroke: '#6b7280',
          strokeWidth: 2
        });
    }
  }

  private getSymbolConfig(symbolId: string): SymbolShapeConfig {
    return this.symbolShapeMap[symbolId] || this.symbolShapeMap['1'];
  }

  // Event handlers
  private handleMarkerDragEnd(e: KonvaEventObject<DragEvent>, marker: PlanMarker): void {
    marker.x = e.target.x();
    marker.y = e.target.y();
    this.markerMoved.emit(marker);
  }

  private handleMarkerClick(e: KonvaEventObject<MouseEvent>, marker: PlanMarker): void {
    e.evt.stopPropagation();
    this.showTooltip(e.evt, marker);
  }

  private handleMarkerRightClick(e: KonvaEventObject<MouseEvent>, marker: PlanMarker): void {
    e.evt.preventDefault();
    this.removeMarker(marker.id);
  }

  private showTooltip(event: MouseEvent, marker: PlanMarker): void {
    this.planState.tooltipMarker = marker;
    this.planState.tooltipPosition = { x: event.clientX, y: event.clientY };
    this.planState.showTooltip = true;
  }

  public hideTooltip(): void {
    this.planState.showTooltip = false;
    this.planState.tooltipMarker = null;
  }

  public removeMarker(markerId: string): void {
    const marker = this.planState.markers.find(m => m.id === markerId);
    if (marker) {
      // Remove from Konva stage
      const konvaMarker = this.stage?.findOne(`#${markerId}`);
      if (konvaMarker) {
        konvaMarker.destroy();
      }

      // Remove from state
      const index = this.planState.markers.findIndex(m => m.id === markerId);
      if (index > -1) {
        this.planState.markers.splice(index, 1);
      }

      this.markerRemoved.emit(markerId);
      this.snackBar.open(`Indikator "${marker.indicator.name}" wurde entfernt`, 'Schließen', { duration: 2000 });
    }
  }

  // Public methods for external control
  public zoomIn(): void {
    if (!this.stage) return;
    const newScale = Math.min(this.stage.scaleX() * 1.25, this.planConfig.maxZoom);
    this.updateZoom(newScale);
  }

  public zoomOut(): void {
    if (!this.stage) return;
    const newScale = Math.max(this.stage.scaleX() * 0.8, this.planConfig.minZoom);
    this.updateZoom(newScale);
  }

  public resetZoom(): void {
    if (!this.stage) return;
    this.updateZoom(1);
  }

  private updateZoom(scale: number): void {
    if (!this.stage) return;
    this.stage.scale({ x: scale, y: scale });
    this.planState.zoomLevel = scale * 100;
    this.zoomChanged.emit(this.planState.zoomLevel);
    this.stage.batchDraw();
  }

  public exportAsImage(format: 'png' | 'jpeg' = 'png'): string {
    if (!this.stage) return '';
    const dataUrl = this.stage.toDataURL({
      mimeType: `image/${format}`,
      quality: 0.9
    });
    this.planExported.emit(dataUrl);
    return dataUrl;
  }

  public exportAsJSON(): string {
    const data = {
      markers: this.planState.markers,
      stageConfig: {
        scale: this.stage?.scaleX() || 1,
        position: this.stage?.position() || { x: 0, y: 0 }
      },
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0'
      }
    };
    return JSON.stringify(data, null, 2);
  }

  public getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  // Drag and Drop Methods for Sidebar Indicators
  onDragStart(event: DragEvent, indicator: RoomIndicator): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', JSON.stringify(indicator));
      event.dataTransfer.effectAllowed = 'copy';
    }
  }

  // Helper methods for indicator display
  getIndicatorSymbolUrl(indicator: RoomIndicator): string | null {
    if (!indicator.indikator?.symbol_id) return null;
    const key = (environment as any).indikatorSymbolMap?.[indicator.indikator.symbol_id];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.png`;
  }

  getIndicatorStatusTextSimple(status: number): string {
    return status === 1 ? 'Aktiv' : 'Inaktiv';
  }

  getIndicatorStatusBadgeClasses(status: number): string {
    if (status === 1) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-red-100 text-red-800';
  }

  // Method to update the plan image URL (called from parent component)
  updatePlanImageUrl(newUrl: string): void {
    console.log('updatePlanImageUrl called:', { newUrl, currentUrl: this.planImageUrl, backgroundLayer: !!this.backgroundLayer });
    
    // Always update the URL
    if (newUrl && newUrl !== this.planImageUrl) {
      this.planImageUrl = newUrl;
      
      // If background layer is ready, load the image immediately
      if (this.backgroundLayer) {
        this.loadPlanImage();
      } else {
        // If not ready, try to re-initialize Konva
        if (!this.stage) {
          console.log('Stage not initialized, trying to initialize Konva');
          this.initializeKonva();
        }
        
        // Wait a bit and try again
        setTimeout(() => {
          if (this.backgroundLayer && this.planImageUrl === newUrl) {
            this.loadPlanImage();
          }
        }, 200);
      }
    }
  }

  // Public method to force re-initialization (can be called from parent)
  public reinitialize(): void {
    console.log('Force re-initializing Konva');
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
      this.backgroundLayer = null;
      this.markersLayer = null;
      this.uiLayer = null;
    }
    setTimeout(() => {
      this.initializeKonva();
    }, 100);
  }
}
